import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { majorToMinor, minorToMajor } from "@src/appV2/lib/Money/utils/minorMajor";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";
import { APP_V2_USER_EVENTS, logEvent, useLogEffect } from "../../../../lib/analytics";
import { Button } from "../../../components/Button";
import { LoadingButton } from "../../../components/LoadingButton";
import { ResponsiveDialog } from "../../../components/ResponsiveDialog";
import { Text } from "../../../components/Text";
import { BerlinThemeProvider } from "../../../theme/ThemeProvider";
import { useCreateSpendingControlNotification } from "../../api/useCreateSpendingControlNotification";
import { type ControlType, CONTROL_TYPES } from "../../types";
import { saveSpendingLimitIncreaseRequest } from "../../utils/spendingLimitRequest";
import { RequestIncreaseDrawer } from "./RequestIncreaseDrawer";
import { SpendingLimitIncreaseRequestForm } from "./SpendingLimitIncreaseRequestForm";

// Lets the dialog footer's submit button target the form rendered in the dialog body (boost flow).
const BOOST_REQUEST_FORM_ID = "spending-limit-increase-request-form";

interface SpendingLimitIncreaseRequestDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  weekStartDate: Date;
  weekRange: string;
  currentLimitInDollars?: number;
  currentLimitInHours?: number;
  /** `total` (default) collects additional hours; `variableCharge` collects dollars for the boost limit. */
  controlType?: ControlType;
  overageInMinorUnits?: number;
  qualification?: string;
  onSuccess?: () => void;
  onBack?: () => void;
  /** Desktop presentation (both are a bottom sheet on mobile): `drawer` (default) is a right-side drawer;
   * `dialog` is the centered charge-editor flow with a Back button. */
  presentation?: "dialog" | "drawer";
}

export function SpendingLimitIncreaseRequestModal(props: SpendingLimitIncreaseRequestDialogProps) {
  const { tu } = useTu("spendingControls");

  const {
    modalState,
    workplaceId,
    weekStartDate,
    weekRange,
    currentLimitInDollars,
    currentLimitInHours,
    controlType = CONTROL_TYPES.total,
    overageInMinorUnits = 0,
    qualification,
    onSuccess,
    onBack,
    presentation = "drawer",
  } = props;

  const isVariableCharge = controlType === CONTROL_TYPES.variableCharge;
  const usesDialog = presentation === "dialog";
  const overageToDisplay = Math.max(0, overageInMinorUnits);
  // Charge-editor entries pass a positive overage; the passive overview opens with none, so copy/prefill differ.
  const hasOverage = overageToDisplay > 0;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: createNotification } = useCreateSpendingControlNotification();

  useLogEffect(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_DRAWER_OPENED, {
    workplaceId,
    weekStartDate: weekStartDate.toISOString(),
    currentLimitInDollars,
    currentLimitInHours,
    ...(isVariableCharge ? { controlType, overageInMinorUnits: overageToDisplay } : {}),
  });

  const handleSubmit = async (amount: number) => {
    setIsSubmitting(true);

    try {
      if (isVariableCharge) {
        const amountInCents = majorToMinor(amount);

        // Namespaced key so a boost request doesn't collide with a `total` request for the same week.
        saveSpendingLimitIncreaseRequest(
          workplaceId,
          weekStartDate,
          amountInCents,
          CONTROL_TYPES.variableCharge
        );

        // Analytics is the durable record (corp acts off this Segment/Datadog event), so log BEFORE the
        // best-effort notification — a failure or hang must not drop or delay the signal.
        logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_SUBMITTED, {
          workplaceId,
          weekStartDate: weekStartDate.toISOString(),
          controlType,
          currentLimitInDollars,
          qualification,
          additionalAmountInDollars: amount,
        });

        try {
          await createNotification({
            workplaceId,
            event: "increaseRequested",
            controlType,
            weekStartDate,
            additionalAmountInCentsRequested: amountInCents,
            qualification,
          });
        } catch {
          // Notification failure is non-critical, continue with the flow
        }

        // Only the dialog (charge-editor) flow toasts; drawer surfaces show the card's "Increase requested" state.
        if (usesDialog) {
          showSuccessToast(tu("Limit increase requested"));
        }
      } else {
        saveSpendingLimitIncreaseRequest(workplaceId, weekStartDate, amount);

        try {
          await createNotification({
            workplaceId,
            event: "increaseRequested",
            weekStartDate,
            additionalHoursRequested: amount,
          });
        } catch {
          // Notification failure is non-critical, continue with the flow
        }

        logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_SUBMITTED, {
          workplaceId,
          weekStartDate: weekStartDate.toISOString(),
          currentLimitInDollars,
          currentLimitInHours,
          additionalHours: amount,
        });
      }

      onSuccess?.();
      modalState.closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  const description = isVariableCharge ? (
    <Stack spacing={1}>
      <Text variant="body1">{tu("Requesting an increase to the weekly rate boost limit.")}</Text>
      {isDefined(currentLimitInDollars) && (
        <Text variant="body1">
          {tu("Current weekly limit: {{limit}}", {
            values: { limit: formatCentsAsUsd(majorToMinor(currentLimitInDollars)) },
          })}
        </Text>
      )}
    </Stack>
  ) : (
    <Text variant="body1">
      {tu("How many additional hours are needed for this week beyond the shifts already posted?")}
    </Text>
  );

  const body = (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Text bold variant="h5">
          {weekRange}
        </Text>
        {description}
      </Stack>

      <SpendingLimitIncreaseRequestForm
        isSubmitting={isSubmitting}
        controlType={controlType}
        // Prefill the breach amount for charge-editor entries; leave blank for the passive overview.
        defaultAmount={
          isVariableCharge && hasOverage ? Math.ceil(minorToMajor(overageToDisplay)) : undefined
        }
        onSubmit={handleSubmit}
        formId={usesDialog ? BOOST_REQUEST_FORM_ID : undefined}
        hideSubmitButton={usesDialog}
      />
    </Stack>
  );

  if (usesDialog) {
    // Back and the X both return to the editor; `modalState.closeModal` is the fallback when no `onBack`.
    const handleBack = onBack ?? modalState.closeModal;
    // Self-wrap in BerlinThemeProvider so the design-system button variants resolve even when hosted in
    // the calendar's legacy MUI theme (the schedule surface already provides the theme).
    return (
      <BerlinThemeProvider transparentBackground>
        <ResponsiveDialog
          showCloseButton
          // Not dismissible: on mobile the BottomSheet's swipe/backdrop/Escape call `closeModal` directly,
          // bypassing `onClose`/`handleBack`, which would strand the editor closed. Force Back/X instead.
          dismissible={false}
          modalState={modalState}
          title={tu("Request limit increase")}
          onClose={handleBack}
          footerContent={
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingX: theme.spacing(5),
                paddingY: theme.spacing(3),
              })}
            >
              <Button variant="outlined" onClick={handleBack}>
                {tu("Back")}
              </Button>
              <LoadingButton
                isLoading={isSubmitting}
                type="submit"
                form={BOOST_REQUEST_FORM_ID}
                variant="primary"
              >
                {tu("Request approval")}
              </LoadingButton>
            </Box>
          }
        >
          {body}
        </ResponsiveDialog>
      </BerlinThemeProvider>
    );
  }

  return (
    <RequestIncreaseDrawer modalState={modalState} title={tu("Request Increase")}>
      {body}
    </RequestIncreaseDrawer>
  );
}
